/**
 * useEntityWorkReports — data hook for the "Rapporte & Aufwand" section
 * (D1 tasks, D2a tenants).
 *
 * Owns the paged report list, the all-time summary, and their loading /
 * error state so the detail pages stay orchestrators (Regel 48).
 * Deliberately NOT a second copy of ``useCalendarWorkReports``: that
 * hook owns the calendar's filter state (assignee / type / date range /
 * "Nur meine"), none of which exists here — this list is pinned to one
 * entity and never filtered.
 *
 * ``scope`` is a CLOSED set, not a registry: each entry names the
 * work-report filter key and the summary client for one entity type.
 * Adding a third is a deliberate edit here, not a plug-in point.
 *
 * Two independent requests on purpose:
 *   GET /work-reports?<key>=…                     the rows (paged)
 *   GET /<entity>/{id}/work-report-summary        the totals (server SUM)
 * The summary is NOT derived from the loaded page — with ten rows shown
 * and forty booked, a page sum would silently understate the effort.
 * They also fail independently: a broken summary must not blank the
 * list, and vice versa.
 *
 * Race protection: a monotonic request counter drops stale responses, so
 * a slow first page cannot overwrite the reload that follows a save —
 * or, on a fast A→B navigation, cannot paint A's rows onto B.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import {
  fetchAdminTaskWorkReportSummary,
  fetchAdminTenantWorkReportSummary,
  fetchAdminWorkReportEffortSummary,
  fetchAdminWorkReports,
} from "../../../../api/adminData";

/**
 * The eight supported scopes — a CLOSED map, not a registry.
 * ``filterKey`` is the ``GET /work-reports`` parameter. Adding a ninth is
 * a deliberate edit here AND in the backend's own closed map; there is
 * no free-form column name.
 *
 * Task and tenant call their own earlier routes (D1/D2a) so those wire
 * contracts stay untouched; the six later entities share the single
 * parameterised effort-summary endpoint rather than adding six paths.
 *
 * D4c3 — what "a property's reports" means. For the seven ENTITY scopes
 * the backend matches on the canonical link table, not on the report's
 * mirrored column (ME2b, ``multi_entity_link_query``), and ME2a already
 * materialised the derived links on write: a report linked to a room
 * carries links to that room's unit and property too. So a property page
 * shows the work booked on the property itself AND the work booked on
 * anything inside it — without this hook joining, walking or deriving
 * anything client-side.
 *
 * The derivation runs one way only (``room → unit → property``), which is
 * the right direction: "worked on this building" must not become "worked
 * in every room of it".
 *
 * And exactly once per page: the filter is a correlated ``EXISTS``, so
 * three link rows pointing at the same property are still one report and
 * its minutes are summed once. The same clause backs the KPI row above
 * the list, so the count and the rows cannot disagree.
 */
const effortSummaryFor = (key) => (entityId) =>
  fetchAdminWorkReportEffortSummary({ [key]: entityId });

export const WORK_REPORT_SCOPES = {
  task: {
    filterKey: "task_id",
    fetchSummary: fetchAdminTaskWorkReportSummary,
  },
  tenant: {
    filterKey: "tenant_id",
    fetchSummary: fetchAdminTenantWorkReportSummary,
  },
  tenancy: {
    filterKey: "tenancy_id",
    fetchSummary: effortSummaryFor("tenancy_id"),
  },
  unit: {
    filterKey: "unit_id",
    fetchSummary: effortSummaryFor("unit_id"),
  },
  application: {
    filterKey: "application_id",
    fetchSummary: effortSummaryFor("application_id"),
  },
  landlord: {
    filterKey: "landlord_id",
    fetchSummary: effortSummaryFor("landlord_id"),
  },
  property: {
    filterKey: "property_id",
    fetchSummary: effortSummaryFor("property_id"),
  },
  room: {
    filterKey: "room_id",
    fetchSummary: effortSummaryFor("room_id"),
  },
};

/**
 * Ten rows: enough that a normal task shows its whole history without a
 * click (most have one to three reports), small enough that a long-running
 * task does not push the comments and activity sections off the screen.
 */
export const WORK_REPORTS_PAGE_SIZE = 10;

/** @param {{scope: keyof WORK_REPORT_SCOPES, entityId: string|undefined}} opts */
export function useEntityWorkReports({ scope, entityId }) {
  const config = WORK_REPORT_SCOPES[scope];
  if (!config) {
    throw new Error(`useEntityWorkReports: unsupported scope "${scope}"`);
  }
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const [loadMoreError, setLoadMoreError] = useState(null);

  // Only the newest request may write state. Without this a reload
  // triggered by a save could be overwritten by the first page that was
  // still in flight when the modal opened.
  const seqRef = useRef(0);

  const loadList = useCallback(async () => {
    if (!entityId) return;
    const seq = ++seqRef.current;
    setLoading(true);
    setError(null);
    setLoadMoreError(null);
    try {
      const data = await fetchAdminWorkReports({
        [config.filterKey]: entityId,
        limit: WORK_REPORTS_PAGE_SIZE,
        offset: 0,
      });
      if (seq !== seqRef.current) return;
      setReports(data.items);
      setTotal(data.total);
    } catch (err) {
      if (seq !== seqRef.current) return;
      setError(err?.message || "Rapporte konnten nicht geladen werden.");
      setReports([]);
      setTotal(0);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [entityId, config.filterKey]);

  const loadSummary = useCallback(async () => {
    if (!entityId) return;
    const seq = seqRef.current;
    setSummaryError(null);
    try {
      const data = await config.fetchSummary(entityId);
      // Same stale guard as the list: on a fast A -> B navigation the
      // late A summary must not overwrite B's numbers.
      if (seq !== seqRef.current) return;
      setSummary(data);
    } catch (err) {
      if (seq !== seqRef.current) return;
      // A failed summary leaves the list fully usable — the KPI row
      // shows its own inline error instead of blanking the section.
      setSummaryError(err?.message || "Aufwand konnte nicht geladen werden.");
    }
  }, [entityId, config]);

  /** Reload BOTH from page 1 — used on mount and after every mutation. */
  const refresh = useCallback(() => {
    return Promise.all([loadList(), loadSummary()]);
  }, [loadList, loadSummary]);

  // Switching entity clears the previous one's rows BEFORE the new load
  // starts, so B never briefly shows A's reports. Declared before the
  // load effect so it runs first on an entityId change.
  useEffect(() => {
    setReports([]);
    setTotal(0);
    setSummary(null);
    setError(null);
    setSummaryError(null);
    setLoadMoreError(null);
  }, [entityId, scope]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const loadMore = useCallback(async () => {
    if (!entityId || loadingMore) return;
    const seq = seqRef.current;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const data = await fetchAdminWorkReports({
        [config.filterKey]: entityId,
        limit: WORK_REPORTS_PAGE_SIZE,
        offset: reports.length,
      });
      if (seq !== seqRef.current) return; // a reload already won
      setReports((prev) => {
        // Dedupe by id: a report created between two pages shifts the
        // window, and the same row must never appear twice.
        const seen = new Set(prev.map((r) => String(r.id)));
        return [...prev, ...data.items.filter((r) => !seen.has(String(r.id)))];
      });
      setTotal(data.total);
    } catch (err) {
      if (seq !== seqRef.current) return;
      // The already-loaded rows stay on screen — a failed next page is
      // not a reason to lose what the user is reading.
      setLoadMoreError(
        err?.message || "Weitere Rapporte konnten nicht geladen werden."
      );
    } finally {
      setLoadingMore(false);
    }
  }, [entityId, reports.length, loadingMore, config.filterKey]);

  return {
    reports,
    total,
    summary,
    loading,
    loadingMore,
    error,
    summaryError,
    loadMoreError,
    hasMore: reports.length < total,
    refresh,
    retry: loadList,
    retrySummary: loadSummary,
    loadMore,
  };
}

export default useEntityWorkReports;
